<template>
  <div class="order-items-tab">
    <!-- Items Section -->
    <div class="items-section">
      <div class="section-header">
        <button
          v-if="order.status == 'completed' && getUserBrandPermissions.order_add_item"
          class="primary-action-btn"
          @click="add_item_modal.show = true"
        >
          {{ $t("globals.add_item") }}
        </button>
      </div>

      <div class="table-wrapper">
        <table class="modern-table items-table">
          <thead>
            <tr>
              <th>{{ $t("globals.product") }}</th>
              <th class="text-right">{{ $t("globals.price_once") }}</th>
              <th class="text-right">{{ $t("globals.price_monthly") }}</th>
              <th class="text-right">{{ $t("globals.quantity") }}</th>
              <th class="text-right">{{ $t("globals.discount") }}</th>
              <th class="text-right">{{ $t("globals.vat") }}</th>
              <th class="text-right">{{ $t("globals.value_no_vat") }}</th>
              <th class="text-right">{{ $t("globals.vat_price") }}</th>
              <th class="text-right">{{ $t("globals.including_btw") }}</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in order.order_items" :key="item.id">
              <td>
                <div class="item-name-cell">
                  <span>{{ item.item_name }}</span>
                  <button
                    v-if="item.price_per_month != 0 && order.status == 'completed' && getUserBrandPermissions.super_admin"
                    class="icon-btn delete-btn"
                    @click="destroyItem(item.id)"
                    title="Delete item"
                  >
                    <v-icon name="trash"></v-icon>
                  </button>
                </div>
              </td>
              <td class="text-right">
                {{ item.price_one_time === 0 ? "" : currencyFormat(item.price_one_time, order.currency) }}
              </td>
              <td class="text-right">
                <div class="price-cell">
                  <span>{{ item.price_per_month === 0 ? "" : currencyFormat(item.price_per_month, order.currency) }}</span>
                  <button
                    v-if="item.price_per_month != 0 && order.status == 'completed' && getUserBrandPermissions.order_item_price_edit"
                    class="icon-btn edit-btn"
                    @click="openOrderItemPriceModal(item.id, item.price_per_month, item.item_name)"
                    title="Edit price"
                  >
                    <v-icon name="wrench"></v-icon>
                  </button>
                  <button
                    v-if="item.price_per_month != null && item.price_per_month > 0 && order.status == 'completed' && getUserBrandPermissions.super_admin"
                    class="icon-btn synch-day-btn"
                    :class="item.synch_units_on_day_of_month ? 'synch-set' : 'edit-btn'"
                    @click="openSynchDayModal(item.id, item.synch_units_on_day_of_month, item.item_name)"
                    :title="item.synch_units_on_day_of_month ? 'Synch on day: ' + item.synch_units_on_day_of_month : 'Set synch day'"
                  >
                    <span v-if="item.synch_units_on_day_of_month" class="day-number">{{ item.synch_units_on_day_of_month }}</span>
                    <svg v-else class="synch-units-icon" viewBox="0 0 24 24" width="18" height="18">
                      <!-- Diagonal line connecting synch to units -->
                      <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" stroke-width="1.5" stroke-dasharray="2,2" opacity="0.3"/>
                      <!-- Top left: Synch symbol (circular arrows) -->
                      <path d="M8 4 L8 8 L4 8" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                      <path d="M4 8 C4 5.5 5.5 3.5 8 3.5" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/>
                      <!-- Bottom right: Unit/box symbol -->
                      <rect x="16" y="16" width="6" height="6" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                      <line x1="17.5" y1="19" x2="20.5" y2="19" stroke="currentColor" stroke-width="1.5"/>
                    </svg>
                  </button>
                </div>
              </td>
              <td class="text-right">
                <div class="quantity-cell">
                  <span>{{ item.quantity }}</span>
                  <button
                    v-if="order.status == 'completed' && getUserBrandPermissions.order_item_quantity_edit"
                    class="icon-btn edit-btn"
                    @click="openOrderItemModal(item.id, item.quantity, item.item_name)"
                    title="Edit quantity"
                  >
                    <v-icon name="wrench"></v-icon>
                  </button>
                </div>
              </td>
              <td class="text-right">{{ currencyFormat(item.discount, order.currency) }}</td>
              <td class="text-right">{{ item.vat.toString() + "%" }}</td>
              <td class="text-right">
                {{ currencyFormat(rowItemTotalWithoutVat(item.price_one_time, item.price_per_month, item.quantity, item.discount, order.contract_duration), order.currency) }}
              </td>
              <td class="text-right">
                {{ currencyFormat(rowItemTotalVatOnly(item.price_one_time, item.price_per_month, item.quantity, order.contract_duration, item.vat, item.discount), order.currency) }}
              </td>
              <td class="text-right">
                {{ currencyFormat(rowItemTotal(item.price_one_time, item.price_per_month, item.quantity, item.vat, item.discount, order.contract_duration), order.currency) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Settings Section -->
    <ulu-settings-table :settings="order.settings" />

    <!-- Notes Section -->
    <div class="notes-section">
      <div class="section-header">
        <h3 class="section-title">{{ $t("globals.notes") }}</h3>
        <button
          v-if="order.status == 'completed' && getUserBrandPermissions.order_note_edit"
          class="primary-action-btn"
          @click="order_notes_modal_show = true"
        >
          {{ $t("globals.edit") }}
        </button>
      </div>
      <textarea
        v-model="order.notes"
        rows="3"
        disabled
        class="notes-textarea"
      ></textarea>
    </div>

    <!-- Modals -->
    <ulu-order-item-quantity-modal
      :show="order_item_modal.show"
      :close="closeOrderItemModal"
      :order_item_id="order_item_modal.id"
      :order_item_quantity="order_item_modal.quantity"
      :order_item_name="order_item_modal.name"
    />
    <ulu-order-item-price-modal
      :show="order_item_price_modal.show"
      :close="closeOrderItemPriceModal"
      :order_item_id="order_item_price_modal.id"
      :order_item_price="order_item_price_modal.price"
      :order_item_name="order_item_price_modal.name"
    />
    <ulu-synch-day-modal
      :show="synch_day_modal.show"
      :close="closeSynchDayModal"
      :order_item_id="synch_day_modal.id"
      :current_day="synch_day_modal.day"
      :order_item_name="synch_day_modal.name"
    />
    <ulu-add-item-modal
      :show="add_item_modal.show"
      :close="closeAddItemModal"
      :currency="order.currency"
    />
    <ulu-order-notes-modal
      :show="order_notes_modal_show"
      :close="closeOrderNotesModal"
      :notes="order.notes"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import {
  rowItemTotalWithoutVat,
  rowItemTotal,
  currencyFormat,
  rowItemTotalVatOnly
} from "@/helpers/price";

export default {
  components: {
    "ulu-settings-table": () => import("@/components/common/SettingsTable"),
    "ulu-order-item-quantity-modal": () =>
      import(
        "@/components/orders/order/order_items_tab/OrderItemQuantityModal"
      ),
    "ulu-order-item-price-modal": () =>
      import("@/components/orders/order/order_items_tab/OrderItemPriceModal"),
    "ulu-synch-day-modal": () =>
      import("@/components/orders/order/order_items_tab/SynchDayModal"),
    "ulu-order-notes-modal": () =>
      import("@/components/orders/order/order_items_tab/OrderNotesModal"),
    "ulu-add-item-modal": () =>
      import("@/components/orders/order/order_items_tab/AddItemModal")
  },
  props: {
    refresh: Function,
    order: {
      type: Object,
      default: () => {
        return {
          status: "",
          currency: "EUR",
          notes: "",
          settings: [],
          contract_duration: 0,
          items: []
        };
      }
    }
  },
  data() {
    return {
      order_notes_modal_show: false,
      order_item_modal: {
        show: false,
        id: null,
        quantity: null,
        name: null
      },
      order_item_price_modal: {
        show: false,
        id: null,
        price: null,
        name: null
      },
      synch_day_modal: {
        show: false,
        id: null,
        day: null,
        name: null
      },
      add_item_modal: {
        show: false
      },
      itemsFields: [
        {
          key: "item_name",
          label: this.$t("globals.item_name")
        },
        {
          key: "price_one_time",
          label: this.$t("globals.price_once")
        },
        {
          key: "price_per_month",
          label: this.$t("globals.price_monthly")
        },
        {
          key: "quantity",
          label: this.$t("globals.quantity")
        },
        {
          key: "discount",
          label: this.$t("globals.discount")
        },
        {
          key: "vat",
          label: this.$t("globals.vat")
        },
        {
          key: "total_no_vat",
          label: this.$t("globals.value_no_vat"),
          class: "text-right"
        },
        {
          key: "vat_price",
          label: this.$t("globals.vat_price"),
          class: "text-right"
        },
        {
          key: "total",
          label: this.$t("globals.total"),
          class: "text-right"
        }
      ]
    };
  },
  methods: {
    ...mapActions("axios_mod", ["simpleMutation"]),
    currencyFormat,
    rowItemTotal,
    rowItemTotalWithoutVat,
    rowItemTotalVatOnly,
    openOrderNotesModal() {
      this.orde_notes_modal.show = true;
    },
    closeOrderNotesModal() {
      this.order_notes_modal_show = false;
      this.refresh();
    },
    openOrderItemModal(id, quantity, name) {
      this.order_item_modal.show = true;
      this.order_item_modal.id = id;
      this.order_item_modal.quantity = quantity;
      this.order_item_modal.name = name;
    },
    openOrderItemPriceModal(id, price, name) {
      this.order_item_price_modal.show = true;
      this.order_item_price_modal.id = id;
      this.order_item_price_modal.price = price;
      this.order_item_price_modal.name = name;
    },
    closeOrderItemModal() {
      this.order_item_modal.show = false;
      this.order_item_modal.id = null;
      this.order_item_modal.quantity = null;
      this.order_item_modal.name = null;
      this.refresh();
    },
    closeOrderItemPriceModal() {
      this.order_item_price_modal.show = false;
      this.order_item_price_modal.id = null;
      this.order_item_price_modal.price = null;
      this.order_item_price_modal.name = null;
      this.refresh();
    },
    closeAddItemModal() {
      this.add_item_modal.show = false;
      this.refresh();
    },
    openSynchDayModal(id, day, name) {
      this.synch_day_modal.show = true;
      this.synch_day_modal.id = id;
      this.synch_day_modal.day = day;
      this.synch_day_modal.name = name;
    },
    closeSynchDayModal() {
      this.synch_day_modal.show = false;
      this.synch_day_modal.id = null;
      this.synch_day_modal.day = null;
      this.synch_day_modal.name = null;
      this.refresh();
    },
    destroyItem(item_id) {
      let data = {
        query: `
          mutation {
            res: OrderItemDelete (
              order_item_id: ${item_id}
            )
          }
        `
      };

      this.simpleMutation({ data, this_ref: this }).then(() => {
        this.refresh();
      });
    }
  },
  computed: {
    ...mapGetters(["getUserBrandPermissions"])
  }
};
</script>

<style scoped>
.order-items-tab {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

/* Items Section - match devices tab (no padding) */
.items-section {
  padding: 0;
  background: transparent;
}

/* Settings Section - match devices tab (no padding) */
.order-items-tab >>> .settings-section {
  padding: 0;
  background: transparent;
}

/* Notes Section - match devices tab (no padding) */
.notes-section {
  padding: 0;
  background: transparent;
}

/* Cell Styles */
.item-name-cell,
.price-cell,
.quantity-cell {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
}

.price-cell,
.quantity-cell {
  justify-content: flex-end;
}

/* Synch Day Button Styles */
.synch-day-btn {
  min-width: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.synch-day-btn .day-number {
  font-weight: 600;
  font-size: 0.875rem;
}

.synch-units-icon {
  display: block;
  opacity: 0.85;
}

.synch-set {
  background-color: #28a745;
  color: white;
}

.synch-set:hover {
  background-color: #218838;
}

/* Notes Textarea */
.notes-textarea {
  width: 100%;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 14px;
  font-size: 0.9375rem;
  font-family: inherit;
  resize: vertical;
  background-color: #f7fafc;
  color: #2d3748;
  line-height: 1.5;
  min-height: 100px;
}

.notes-textarea:disabled {
  cursor: not-allowed;
  opacity: 0.85;
}

/* Responsive Design */
@media (max-width: 768px) {
  .order-items-tab {
    gap: 24px;
  }

  .order-items-tab >>> .settings-section {
    padding: 16px;
  }

  .item-name-cell,
  .price-cell,
  .quantity-cell {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
  }

  .price-cell,
  .quantity-cell {
    align-items: flex-end;
  }
}
</style>
